import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Trash2, Save, Receipt, Bell, PiggyBank, LocateFixed } from "lucide-react";
import client, { apiErr, money } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const LOYALTY = ["PC Optimum", "Scene+", "More Rewards", "Co-op Membership", "Costco Membership"];

export default function Profile() {
  const { user, prefs, loadPrefs, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ins, setIns] = useState(null);

  useEffect(() => { setD(prefs ? { ...prefs } : {}); }, [prefs]);
  useEffect(() => { client.get("/insights").then((r) => setIns(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>;

  const up = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setD((p) => ({ ...p, [k]: (p[k] || []).includes(v) ? p[k].filter((x) => x !== v) : [...(p[k] || []), v] }));

  const save = async () => {
    setBusy(true);
    try { await client.put("/preferences", { data: d }); await loadPrefs(); toast.success("Preferences saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const exportData = async () => {
    const { data } = await client.get("/auth/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "pantry-pilot-data.json"; a.click();
    toast.success("Data exported");
  };
  const deleteAccount = async () => {
    try { await client.delete("/auth/account"); toast.success("Account deleted"); await logout(); }
    catch { toast.error("Could not delete account"); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try { await client.post("/live/locate", { lat: pos.coords.latitude, lng: pos.coords.longitude }); await loadPrefs(); toast.success("Location updated"); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    }, () => toast.error("Location permission was denied"));
  };

  const Section = ({ title, children }) => (
    <div className="bg-white rounded-3xl p-5 pp-shadow space-y-4"><h2 className="font-extrabold text-pp-text">{title}</h2>{children}</div>
  );
  const Row = ({ label, value }) => (
    <div><div className="flex justify-between text-sm font-medium"><span>{label}</span><span className="text-pp-forest font-bold">{value}</span></div></div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <PageHero image="/img/crates.jpg" eyebrow="Your account" title={user?.name || "Profile"} subtitle={user?.email} testId="profile-hero">
        <div className="flex flex-wrap gap-3">
          <div className="rounded-2xl bg-white/15 backdrop-blur-md px-4 py-2" data-testid="profile-lifetime-saved"><p className="text-xs text-white/75">Total saved</p><p className="text-2xl font-extrabold text-pp-gold">{money(ins?.lifetime_saved || 0)}</p></div>
          <div className="rounded-2xl bg-white/15 backdrop-blur-md px-4 py-2"><p className="text-xs text-white/75">This month</p><p className="text-2xl font-extrabold">{money(ins?.month_saved || 0)}</p></div>
          <div className="rounded-2xl bg-white/15 backdrop-blur-md px-4 py-2"><p className="text-xs text-white/75">Trips</p><p className="text-2xl font-extrabold">{ins?.trips_count || 0}</p></div>
        </div>
        <Button onClick={() => navigate("/savings")} data-testid="profile-view-savings" className="mt-3 rounded-full bg-pp-gold text-pp-text hover:bg-pp-gold/90 font-bold"><PiggyBank className="h-4 w-4 mr-1" /> Budget & insights</Button>
      </PageHero>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-extrabold text-pp-text">Settings</h2>
        <Button onClick={save} disabled={busy} data-testid="save-preferences" className="rounded-full bg-pp-forest">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
        </Button>
      </div>

      <Section title="Location">
        <Button variant="outline" onClick={useMyLocation} data-testid="use-my-location" className="w-full rounded-full bg-white"><LocateFixed className="h-4 w-4 mr-1" /> Use my current location</Button>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Address</Label><Input className="mt-1 bg-white" value={d.home_address || ""} onChange={(e) => up("home_address", e.target.value)} /></div>
          <div><Label>Postal code</Label><Input className="mt-1 bg-white" value={d.postal_code || ""} onChange={(e) => up("postal_code", e.target.value)} /></div>
          <div><Label>City</Label><Input className="mt-1 bg-white" value={d.city || ""} onChange={(e) => up("city", e.target.value)} /></div>
          <div><Label>Household size</Label><Input type="number" className="mt-1 bg-white" value={d.household_size || 1} onChange={(e) => up("household_size", +e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Trip optimization">
        <Row label="Max driving radius" value={`${d.max_radius || 20} km`} />
        <Slider min={2} max={40} step={1} value={[d.max_radius || 20]} onValueChange={([v]) => up("max_radius", v)} data-testid="pref-radius" />
        <Row label="Max stores per trip" value={d.max_stores || 3} />
        <Slider min={1} max={5} step={1} value={[d.max_stores || 3]} onValueChange={([v]) => up("max_stores", v)} data-testid="pref-maxstores" />
        <Row label="Min savings to add a stop" value={`$${d.min_savings ?? 5}`} />
        <Slider min={0} max={25} step={1} value={[d.min_savings ?? 5]} onValueChange={([v]) => up("min_savings", v)} data-testid="pref-minsavings" />
        <div className="grid sm:grid-cols-3 gap-3 pt-2">
          <div><Label>Fuel L/100km</Label><Input type="number" step="0.1" className="mt-1 bg-white" value={d.fuel_efficiency || 9} onChange={(e) => up("fuel_efficiency", +e.target.value)} /></div>
          <div><Label>Fuel $/L</Label><Input type="number" step="0.01" className="mt-1 bg-white" value={d.fuel_price || 1.55} onChange={(e) => up("fuel_price", +e.target.value)} /></div>
          <div><Label>Value of time $/hr</Label><Input type="number" className="mt-1 bg-white" value={d.time_value || 15} onChange={(e) => up("time_value", +e.target.value)} /></div>
        </div>
        <label className="flex items-center justify-between rounded-2xl bg-pp-cream p-3 mt-1">
          <span className="text-sm font-medium">Include warehouse clubs</span>
          <Switch checked={d.warehouse_ok !== false} onCheckedChange={(v) => up("warehouse_ok", v)} data-testid="pref-warehouse" />
        </label>
        <label className="flex items-center justify-between rounded-2xl bg-pp-cream p-3">
          <span className="text-sm font-medium">Generic / store brands OK</span>
          <Switch checked={d.generic_ok !== false} onCheckedChange={(v) => up("generic_ok", v)} data-testid="pref-generic" />
        </label>
      </Section>

      <Section title="Loyalty programs">
        <div className="flex flex-wrap gap-2">
          {LOYALTY.map((l) => (
            <button key={l} onClick={() => toggle("loyalty_programs", l)} data-testid={`pref-loyalty-${l}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${(d.loyalty_programs || []).includes(l) ? "bg-pp-forest text-white border-pp-forest" : "bg-white border-pp-border text-pp-text"}`}>{l}</button>
          ))}
        </div>
      </Section>

      <Section title="Quick links">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="bg-white rounded-2xl h-11" onClick={() => navigate("/receipts")} data-testid="link-receipts"><Receipt className="h-4 w-4 mr-1" /> Receipts</Button>
          <Button variant="outline" className="bg-white rounded-2xl h-11" onClick={() => navigate("/alerts")} data-testid="link-alerts"><Bell className="h-4 w-4 mr-1" /> Alerts</Button>
        </div>
      </Section>

      <Section title="Privacy & data">
        <Button variant="outline" onClick={exportData} data-testid="export-data" className="w-full bg-white rounded-full"><Download className="h-4 w-4 mr-1" /> Export my data</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" data-testid="delete-account" className="w-full text-pp-error rounded-full"><Trash2 className="h-4 w-4 mr-1" /> Delete account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes your lists, trips, receipts and preferences. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} className="bg-pp-error">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Section>
    </div>
  );
}
